/**
 * Shared lifecycle for party-game host displays:
 * question/result/end events, local countdown, live answers, host-play submit, restart.
 */
import { useState, useEffect, useRef, useCallback, type Dispatch, type SetStateAction, type MutableRefObject } from 'react';
import type { WebSocketAdapter } from '../utils/WebSocketAdapter';
import type { GameStatistics } from '../types/game';
import { useGameEndScreenData } from './useGameEndScreenData';

export type PartyLeaderboardEntry = {
  playerId: string;
  playerName: string;
  score: number;
};

export type PartyLiveAnswer = {
  playerId: string;
  playerName: string;
  avatar?: string;
  timestamp: number;
  answer?: string;
};

export type PartyQuestionBase = {
  questionId?: string;
  id?: string;
  timeLimit: number;
};

export type PartyRoomStateBase<TQuestion extends PartyQuestionBase = PartyQuestionBase> = {
  roomName: string;
  gameState: 'waiting' | 'playing' | 'finished';
  players: Array<{ id: string; name: string; score: number; avatar?: string }>;
  currentQuestion?: TQuestion;
  finalLeaderboard?: PartyLeaderboardEntry[];
  gameStatistics?: GameStatistics | null;
};

export type QuestionResultApi = {
  /** Reveal result immediately (clears current question). */
  showResult: () => void;
  /**
   * Schedule reveal after delayMs. Auto-cancelled on new question, return-to-waiting,
   * game-ended, game-started, or unmount — so a late timeout cannot wipe the next question.
   */
  scheduleShowResult: (delayMs: number, onRevealed?: () => void) => void;
};

export type UsePartyHostGameLifecycleOptions<
  TQuestion extends PartyQuestionBase,
  TResult,
  TAnswerReceipt = { voteRecorded?: boolean; answerCorrect?: boolean; isCorrect?: boolean },
> = {
  roomState: PartyRoomStateBase<TQuestion>;
  socket: WebSocketAdapter;
  hostIsPlaying?: boolean;
  isQuestionForGame: (question: TQuestion) => boolean;
  isResultForGame: (result: TResult) => boolean;
  /** Default: vote recorded, or graded answer receipt (isCorrect / answerCorrect boolean). */
  isHostAnswerAccepted?: (receipt: TAnswerReceipt) => boolean;
  /** Custom result handling (e.g. Stoned Yet delayed reveal). Default: show result immediately. */
  onQuestionResult?: (result: TResult, api: QuestionResultApi) => void;
  /** Extra reset when a new question starts / host answer resets */
  onResetHostPlaying?: () => void;
  /** Game-specific handling for game-ended payloads. */
  onGameEnded?: (data: {
    leaderboard: PartyLeaderboardEntry[];
    statistics?: GameStatistics;
    [key: string]: unknown;
  }) => void;
  /** Hot Takes-style custom handling after answer-received. */
  onAnswerReceived?: (
    data: TAnswerReceipt & { questionId?: string },
    api: { addLiveAnswer: (answer: PartyLiveAnswer) => void }
  ) => void;
};

export type PartyHostGameLifecycle<
  TQuestion extends PartyQuestionBase,
  TResult,
> = {
  currentQuestion: TQuestion | null;
  timeRemaining: number;
  questionResult: TResult | null;
  gameEnded: boolean;
  waitingForNewGame: boolean;
  finalLeaderboard: PartyLeaderboardEntry[];
  gameStatistics: GameStatistics | null;
  liveAnswers: PartyLiveAnswer[];
  answerSubmitted: boolean;
  roomNameRef: MutableRefObject<string>;
  playersRef: MutableRefObject<PartyRoomStateBase['players']>;
  gameEndData: ReturnType<typeof useGameEndScreenData>;
  setLiveAnswers: Dispatch<SetStateAction<PartyLiveAnswer[]>>;
  setAnswerSubmitted: Dispatch<SetStateAction<boolean>>;
  setWaitingForNewGame: Dispatch<SetStateAction<boolean>>;
  setGameEnded: Dispatch<SetStateAction<boolean>>;
  setFinalLeaderboard: Dispatch<SetStateAction<PartyLeaderboardEntry[]>>;
  setCurrentQuestion: Dispatch<SetStateAction<TQuestion | null>>;
  setQuestionResult: Dispatch<SetStateAction<TResult | null>>;
  submitAnswer: (answer: string) => boolean;
  restartGame: () => void;
  playSameGame: () => void;
  resetHostPlayingState: () => void;
};

function defaultQuestionId(q: PartyQuestionBase): string | undefined {
  return q.questionId || q.id;
}

/** True when the server acknowledged the host's submit (vote or graded answer). */
export function defaultIsHostAnswerAccepted(data: {
  voteRecorded?: boolean;
  answerCorrect?: boolean;
  isCorrect?: boolean;
}): boolean {
  if (data.voteRecorded === true) return true;
  // Graded receipt — submitted whether correct or wrong
  if (typeof data.answerCorrect === 'boolean') return true;
  if (typeof data.isCorrect === 'boolean') return true;
  return false;
}

export function usePartyHostGameLifecycle<
  TQuestion extends PartyQuestionBase,
  TResult extends { questionId?: string },
  TAnswerReceipt extends {
    voteRecorded?: boolean;
    answerCorrect?: boolean;
    isCorrect?: boolean;
    questionId?: string;
  } = {
    voteRecorded?: boolean;
    answerCorrect?: boolean;
    isCorrect?: boolean;
    questionId?: string;
  },
>(
  options: UsePartyHostGameLifecycleOptions<TQuestion, TResult, TAnswerReceipt>
): PartyHostGameLifecycle<TQuestion, TResult> {
  const {
    roomState,
    socket,
    hostIsPlaying = false,
    isQuestionForGame,
    isResultForGame,
    isHostAnswerAccepted,
    onQuestionResult,
    onResetHostPlaying,
    onGameEnded,
    onAnswerReceived,
  } = options;

  const [currentQuestion, setCurrentQuestion] = useState<TQuestion | null>(
    roomState.currentQuestion || null
  );
  const [timeRemaining, setTimeRemaining] = useState<number>(
    roomState.currentQuestion?.timeLimit || 0
  );
  const [questionResult, setQuestionResult] = useState<TResult | null>(null);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<PartyLeaderboardEntry[]>([]);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const [waitingForNewGame, setWaitingForNewGame] = useState(false);
  const [liveAnswers, setLiveAnswers] = useState<PartyLiveAnswer[]>([]);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);

  const previousQuestionIdRef = useRef<string | undefined>(undefined);
  const roomNameRef = useRef(roomState.roomName);
  const playersRef = useRef(roomState.players);
  const isSubmittingRef = useRef(false);
  const resultRevealTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resultRevealGenRef = useRef(0);
  const gameEndData = useGameEndScreenData(roomState);

  const isQuestionForGameRef = useRef(isQuestionForGame);
  const isResultForGameRef = useRef(isResultForGame);
  const isHostAnswerAcceptedRef = useRef(isHostAnswerAccepted);
  const onQuestionResultRef = useRef(onQuestionResult);
  const onAnswerReceivedRef = useRef(onAnswerReceived);
  const onGameEndedRef = useRef(onGameEnded);
  const onResetHostPlayingRef = useRef(onResetHostPlaying);
  isQuestionForGameRef.current = isQuestionForGame;
  isResultForGameRef.current = isResultForGame;
  isHostAnswerAcceptedRef.current = isHostAnswerAccepted;
  onQuestionResultRef.current = onQuestionResult;
  onAnswerReceivedRef.current = onAnswerReceived;
  onGameEndedRef.current = onGameEnded;
  onResetHostPlayingRef.current = onResetHostPlaying;

  const cancelScheduledResultReveal = useCallback(() => {
    if (resultRevealTimeoutRef.current != null) {
      clearTimeout(resultRevealTimeoutRef.current);
      resultRevealTimeoutRef.current = null;
    }
    resultRevealGenRef.current += 1;
  }, []);

  const resetHostPlayingState = useCallback(() => {
    setAnswerSubmitted(false);
    setLiveAnswers([]);
    isSubmittingRef.current = false;
    onResetHostPlayingRef.current?.();
  }, []);

  const beginQuestion = useCallback(
    (data: TQuestion) => {
      cancelScheduledResultReveal();
      setGameEnded(false);
      setCurrentQuestion(data);
      setTimeRemaining(data.timeLimit);
      setQuestionResult(null);
      setWaitingForNewGame(false);
      resetHostPlayingState();
      previousQuestionIdRef.current = defaultQuestionId(data);
    },
    [cancelScheduledResultReveal, resetHostPlayingState]
  );

  useEffect(() => {
    roomNameRef.current = roomState.roomName;
  }, [roomState.roomName]);

  useEffect(() => {
    playersRef.current = roomState.players;
  }, [roomState.players]);

  // Always clear pending reveal on unmount
  useEffect(() => () => cancelScheduledResultReveal(), [cancelScheduledResultReveal]);

  const roomGameState = roomState.gameState;
  useEffect(() => {
    if (roomGameState === 'finished' && gameEndData) {
      cancelScheduledResultReveal();
      setGameEnded(true);
      setCurrentQuestion(null);
      setQuestionResult(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gameEndData is derived from roomState
  }, [roomGameState, gameEndData, cancelScheduledResultReveal]);

  useEffect(() => {
    if (roomGameState === 'finished') return;
    const question = roomState.currentQuestion;
    if (!question) return;
    const questionId = defaultQuestionId(question);
    if (!questionId || previousQuestionIdRef.current === questionId) return;
    beginQuestion(question);
  }, [roomState.currentQuestion, roomGameState, beginQuestion]);

  useEffect(() => {
    const handleQuestion = (data: TQuestion) => {
      if (!isQuestionForGameRef.current(data)) return;
      beginQuestion(data);
    };

    const handleQuestionResult = (result: TResult) => {
      if (!isResultForGameRef.current(result)) return;

      const showResult = () => {
        setQuestionResult(result);
        setCurrentQuestion(null);
      };

      const scheduleShowResult = (delayMs: number, onRevealed?: () => void) => {
        cancelScheduledResultReveal();
        const gen = resultRevealGenRef.current;
        resultRevealTimeoutRef.current = setTimeout(() => {
          resultRevealTimeoutRef.current = null;
          // Stale after cancel / newer schedule / new question
          if (gen !== resultRevealGenRef.current) return;
          showResult();
          onRevealed?.();
        }, delayMs);
      };

      if (onQuestionResultRef.current) {
        onQuestionResultRef.current(result, { showResult, scheduleShowResult });
      } else {
        cancelScheduledResultReveal();
        showResult();
      }
    };

    const handleGameEnded = (data: {
      leaderboard: PartyLeaderboardEntry[];
      statistics?: GameStatistics;
      [key: string]: unknown;
    }) => {
      cancelScheduledResultReveal();
      setGameEnded(true);
      const sorted = [...(data.leaderboard || [])].sort((a, b) => b.score - a.score);
      setFinalLeaderboard(sorted);
      setGameStatistics(data.statistics || null);
      setCurrentQuestion(null);
      setQuestionResult(null);
      onGameEndedRef.current?.(data);
    };

    const handleReturnToWaiting = () => {
      cancelScheduledResultReveal();
      setWaitingForNewGame(true);
      setGameEnded(false);
      setCurrentQuestion(null);
      setQuestionResult(null);
      setGameStatistics(null);
    };

    const handleGameStarted = () => {
      cancelScheduledResultReveal();
      setWaitingForNewGame(false);
      setGameEnded(false);
      setGameStatistics(null);
      setFinalLeaderboard([]);
      setQuestionResult(null);
      // Keep currentQuestion — next `question` event (or room-state) sets it; avoids loading flash
      resetHostPlayingState();
    };

    const handlePlayerAnswered = (data: PartyLiveAnswer) => {
      setLiveAnswers((prev) => {
        if (prev.some((a) => a.playerId === data.playerId)) return prev;
        return [...prev, data];
      });
    };

    const handleAnswerReceived = (data: TAnswerReceipt) => {
      if (hostIsPlaying) {
        const accepted = isHostAnswerAcceptedRef.current
          ? isHostAnswerAcceptedRef.current(data)
          : defaultIsHostAnswerAccepted(data);
        if (accepted) setAnswerSubmitted(true);
      }
      onAnswerReceivedRef.current?.(data, {
        addLiveAnswer: (answer) => {
          setLiveAnswers((prev) => {
            if (prev.some((existing) => existing.playerId === answer.playerId)) return prev;
            return [...prev, answer];
          });
        },
      });
    };

    socket.on('question', handleQuestion);
    socket.on('question-result', handleQuestionResult);
    socket.on('game-ended', handleGameEnded);
    socket.on('return-to-waiting', handleReturnToWaiting);
    socket.on('game-started', handleGameStarted);
    socket.on('player-answered', handlePlayerAnswered);
    socket.on('answer-received', handleAnswerReceived);

    return () => {
      socket.off('question', handleQuestion);
      socket.off('question-result', handleQuestionResult);
      socket.off('game-ended', handleGameEnded);
      socket.off('return-to-waiting', handleReturnToWaiting);
      socket.off('game-started', handleGameStarted);
      socket.off('player-answered', handlePlayerAnswered);
      socket.off('answer-received', handleAnswerReceived);
    };
  }, [socket, hostIsPlaying, resetHostPlayingState, beginQuestion, cancelScheduledResultReveal]);

  useEffect(() => {
    if (!currentQuestion || questionResult) return;

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 100) {
          clearInterval(interval);
          return 0;
        }
        return prev - 100;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [currentQuestion, questionResult]);

  const submitAnswer = useCallback(
    (answer: string): boolean => {
      if (answerSubmitted || !currentQuestion || isSubmittingRef.current) return false;
      const questionId = defaultQuestionId(currentQuestion);
      if (!questionId) return false;

      isSubmittingRef.current = true;
      socket.emit('submit-answer', {
        roomName: roomNameRef.current,
        questionId,
        answer,
        timestamp: Date.now(),
      });
      return true;
    },
    [answerSubmitted, currentQuestion, socket]
  );

  const restartGame = useCallback(() => {
    cancelScheduledResultReveal();
    setWaitingForNewGame(true);
    setGameEnded(false);
    setFinalLeaderboard([]);
    setCurrentQuestion(null);
    setQuestionResult(null);
    resetHostPlayingState();
    socket.emit('start-game', { roomName: roomState.roomName });
  }, [cancelScheduledResultReveal, resetHostPlayingState, roomState.roomName, socket]);

  const playSameGame = useCallback(() => {
    cancelScheduledResultReveal();
    setGameEnded(false);
    setFinalLeaderboard([]);
    setCurrentQuestion(null);
    setQuestionResult(null);
    setWaitingForNewGame(true);
    resetHostPlayingState();
    socket.emit('start-game', { roomName: roomState.roomName });
  }, [cancelScheduledResultReveal, resetHostPlayingState, roomState.roomName, socket]);

  return {
    currentQuestion,
    timeRemaining,
    questionResult,
    gameEnded,
    waitingForNewGame,
    finalLeaderboard,
    gameStatistics,
    liveAnswers,
    answerSubmitted,
    roomNameRef,
    playersRef,
    gameEndData,
    setLiveAnswers,
    setAnswerSubmitted,
    setWaitingForNewGame,
    setGameEnded,
    setFinalLeaderboard,
    setCurrentQuestion,
    setQuestionResult,
    submitAnswer,
    restartGame,
    playSameGame,
    resetHostPlayingState,
  };
}
